/**
 * paperItemsLayout — 시험지 [문항] 탭이 편집기 폭에 따라 **몇 칸으로 설지** 정하는 자.
 *
 * 폭 단계는 넷이다(오너 결정 2026-09-15 D1 A · docs/exam-builder-v2/0915_SPEC_paper-items-wide-layout.html).
 *   770 미만        한 칸   — 통계가 위, 문항 목록이 아래
 *   770 ~ 1,261     두 칸   — 왼쪽 통계(분포 3열 + 개념 목록) · 오른쪽 문항 카드 1열
 *   1,262 ~ 1,545   카드 2열 — 통계는 같은 배치로 폭만 준다
 *   1,546 이상      넓음    — 통계가 「분포 | 개념 목록」 두 칸으로 갈린다
 *
 * **카드 2열이 통계 나눔보다 먼저다**(오너 2026-09-15 「통계쪽 2열보다는 문제카드 2열 매치가 우선」).
 * 나눔은 카드가 2열로 선 뒤 남는 통계 칸이 두 칸을 받칠 만할 때만 연다 — 폭이 줄 때도 나눔이 먼저 풀린다.
 *
 * 문턱은 새 숫자가 아니라 기존 값에서 낸다. 원칙은 paperColumn.js 와 같다 —
 * **새 칸을 세워도 어떤 칸도 지금의 하한보다 좁아지지 않을 때만** 세운다.
 * 폭은 뷰포트가 아니라 편집기 패널의 실제 폭이다(사이드바를 접으면 같은 화면에서도 90px 달라진다 —
 * PaperItemsEditor 머리 주석). 2026-09-15 실측: 사이드바를 편 상태에서 편집기 폭 = 화면 폭 − 480.
 */
import { PAPER_ITEM_ROW_MAX_PX } from './PaperItemPreviewRow';

/**
 * 2열 하한 — 목록 485 + 사이 16 + 통계 최소 폭. 이 아래에서는 통계가 목록 위로 올라간다(1열).
 * (PaperItemsEditor 에 있던 것을 값·근거 그대로 옮겼다 — 문턱이 셋이 되어 한 곳에서 서로의 셈이 보여야 한다.)
 *
 * 통계 최소를 **300 → 268** 로 내렸다(오너 지적 2026-08-29 「폭이 충분한데도 1열로 보인다」).
 * 300 은 어림값이었다. 실제로 **무엇이 먼저 깨지는지**를 재 보면 그것은 막대가 아니라
 * **난이도 라벨**이다 — 통계 열을 2px 씩 좁혀 가며 잰 값:
 *
 *   통계 280 → 라벨 잘림 5 / 13   (「확률·통계」 등 긴 이름 — 300 에서도 이미 잘리던 것들)
 *   통계 268 → 라벨 잘림 5 / 13   ← **여기가 마지막 지점**
 *   통계 266 → 라벨 잘림 9 / 13   («Lv.2»~«Lv.5» 가 「Lv…」 로 넘어간다)
 *   통계 256 → 라벨 잘림 13 / 13  («Lv.1» 까지 잘려 다섯 줄이 구분되지 않는다)
 *
 * 268 을 고른 이유는 **넓을 때와 같은 것만 잘리는 마지막 폭**이기 때문이다. 긴 영역 이름이
 * 줄어드는 것은 300 에서도 일어나던 일이라 새로 잃는 것이 없고, 그 아래로 한 단만 더 가면
 * 난이도 1~5 가 전부 「Lv…」 가 되어 다섯 줄이 같은 글자가 된다.
 * (막대는 이보다 먼저 min-w 16px 에 닿지만, 길이가 아니라 옆의 숫자가 값을 나르므로 하한이
 *  아니다. 라벨은 잘리면 그 줄이 무엇인지 알 방법이 남지 않는다.)
 *
 * 485 + 16 + 268 = 769 에서 **770**. 더 내리려면 통계를 3열에서 접는 등 구조를 바꿔야 한다.
 */
export const TWO_COL_MIN_PX = 770;

/** 통계 칸과 목록 칸 사이 — 편집기 격자의 gap-4. 한쪽만 바꾸면 아래 문턱 셈이 어긋난다 */
export const PANEL_GAP_PX = 16;
/** 통계 칸 최소 폭 — 위 770 의 셈(485 + 16 + 268 = 769)에 들어 있는 실측값 */
export const STATS_MIN_PX = 268;
/**
 * 카드와 카드 사이 가로 간격 — 목록 ul 의 gap-x-2.
 * 카드끼리는 한 목록이라 통계 칸과의 사이(16)보다 가까워야 하고(간격이 소속을 말한다),
 * 세로 간격(gap-y-1 · 4)과 같은 계열에서 한 단 위다.
 */
export const CARD_GAP_PX = 8;
/** 카드 2열일 때 목록 칸 폭 = 485 × 2 + 8 = 978 */
export const LIST_TWO_PX = PAPER_ITEM_ROW_MAX_PX * 2 + CARD_GAP_PX;
/** 카드 2열 문턱 — 목록 978 을 세우고도 통계가 268 아래로 줄지 않는 첫 폭 = 1,262 */
export const CARDS_TWO_MIN_PX = LIST_TWO_PX + PANEL_GAP_PX + STATS_MIN_PX;

/**
 * 통계 카드가 안쪽 폭 바깥에 두르는 것 — PaperStatsPanel 몸체의 여백 p-4 좌우 32 + 테두리 좌우 2.
 * 268 도 이 둘을 포함한 칸 폭이라, 통계가 가장 좁을 때의 안쪽 폭은 268 − 34 = 234 다
 * (2026-09-15 실측: 편집기 1,262 에서 개념 목록 234px).
 */
export const STATS_CHROME_PX = 34;
/** 나눈 두 칸 사이 — PaperStatsPanel 두 칸 몸체의 gap-x-4 */
const STATS_SPLIT_GAP_PX = 16;
/** 개념 칸 앞 세로선 1 + 왼쪽 여백 pl-4 16 — PaperStatsPanel 개념 칸의 border-l pl-4 */
export const STATS_DIVIDER_PX = 17;
/**
 * 나눈 통계 칸의 최소 폭 — 분포 칸과 개념 목록이 둘 다 **통계가 가장 좁을 때의 안쪽 폭(234)** 이상.
 * 두 칸은 반반(1fr 1fr)이라 한 칸 = 234 + 17(개념 칸의 선·여백) = 251 → 34 + 251 × 2 + 16 = 552.
 * (설계 문서 판 1 은 테두리 2px 을 빼 554 · 1,548 로 적었다 — 구현 실측으로 바로잡았다.)
 */
export const STATS_SPLIT_MIN_PX = STATS_CHROME_PX + (STATS_MIN_PX - STATS_CHROME_PX + STATS_DIVIDER_PX) * 2 + STATS_SPLIT_GAP_PX;
/** 통계 나눔 문턱 = 978 + 16 + 552 = 1,546 */
export const STATS_SPLIT_EDITOR_MIN_PX = LIST_TWO_PX + PANEL_GAP_PX + STATS_SPLIT_MIN_PX;

/**
 * 편집기 폭 → 배치.
 *   twoCol     통계와 목록이 좌우로 서는가
 *   listCols   문항 카드 열 수(1 · 2)
 *   statsSplit 통계를 「분포 | 개념 목록」 두 칸으로 나누는가 — **listCols 가 2 일 때만** 참이다
 *   listW      오른쪽 목록 칸 폭(px) — 1열 485 · 2열 978
 * 아직 재지 못한 폭(0·NaN)은 한 칸이다 — 종전 첫 페인트와 같다.
 */
export function itemsLayout(panelW) {
  const w = Number(panelW) || 0;
  const twoCol = w >= TWO_COL_MIN_PX;
  const listCols = twoCol && w >= CARDS_TWO_MIN_PX ? 2 : 1;
  // 카드 2열이 먼저다 — 나눔은 카드가 2열일 때만 본다
  const statsSplit = listCols === 2 && w >= STATS_SPLIT_EDITOR_MIN_PX;
  return { twoCol, listCols, statsSplit, listW: listCols === 2 ? LIST_TWO_PX : PAPER_ITEM_ROW_MAX_PX };
}

export default itemsLayout;
